import { ChangeEvent, DragEvent, useContext, useRef, useState } from "react";
import { LangContext, LangContextType, LoadingContext, LoadingContextType, ToastContext, ToastContextType } from "../../utilities/customHooks";
import instance from "../../middleware/HTTPInterceptors";
import textData from "../../textData.json";
import { getUploadAuthHeader } from "../../utilities/helperFunctions";
import { CloudUpload, FileText } from "lucide-react";

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface ChildComponentProps {
  handlePdfStateChange: () => void;
}

const FileUpload: React.FC<ChildComponentProps> = ({ handlePdfStateChange }) => {
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;
  const { langState } = useContext(LangContext) as LangContextType;
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndUpload = (file: File) => {
    if (file.type !== "application/pdf") {
      showToast({ toastType: "failure", message: "Please select a PDF file." });
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showToast({ toastType: "failure", message: `File must be under ${MAX_FILE_SIZE_MB}MB.` });
      return;
    }
    setFileName(file.name);
    handleFileUpload(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    validateAndUpload(e.target.files[0]);
    e.target.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = async (selectedFile: File) => {
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      showLoading();
      const authHeaders = await getUploadAuthHeader();
      const response = await instance.post("/api/public/upload", formData, {
        headers: authHeaders
      });
      hideLoading();
      if (response.status === 200) {
        handlePdfStateChange();
        showToast({ toastType: "success", message: "Pitch Deck Successfully Uploaded!" });
      } else {
        showToast({ toastType: "failure", message: "Unable to Upload, please try again. If the error persists please contact support." });
      }
    } catch {
      hideLoading();
      setFileName(null);
      showToast({ toastType: "failure", message: "Unable to Upload, please try again. If the error persists please contact support." });
    }
  };

  return (
    <div className="flex flex-col">
      <label
        htmlFor="PitchDeckUpload"
        className={`flex max-w-xl w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 cursor-pointer transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <CloudUpload className="size-10 text-muted-foreground" />
        <div className="text-center">
          <p className="text-sm">
            {textData.Pages.FounderPages.FounderProfile.Drag[langState]}{" "}
            <span className="font-medium text-primary underline underline-offset-2">
              {textData.Pages.FounderPages.FounderProfile.Browse[langState]}
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {textData.Pages.FounderPages.FounderProfile.Pdf[langState]} · Max {MAX_FILE_SIZE_MB}MB
          </p>
        </div>
        {fileName && (
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <FileText className="size-4" />
            <span className="truncate max-w-xs">{fileName}</span>
          </div>
        )}
      </label>
      <input
        ref={inputRef}
        id="PitchDeckUpload"
        className="hidden"
        type="file"
        accept="application/pdf"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileUpload;
